import { DeadlineFormDto, type DeadlineFormInterface } from '@zaoblako/shared/common/dtos/deadline/deadline-form.dto';
import { PlanningUnitFormDto, type PlanningUnitFormInterface } from '@zaoblako/shared/common/dtos/planning-unit/planning-unit-form.dto';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { ProjectDtoKeysEnum } from '@zaoblako/shared/modules/project/enum/project-dto-keys.enum';
import { idLikeSchema } from '@zaoblako/shared/modules/serde/schemas/id-like.schema';
import { ProjectCheckersEnum } from '@zaoblako/shared/modules/project/enum/project-checkers.enum';
import { nonEmptyStringSchema } from '@zaoblako/shared/modules/serde/schemas/non-empty-string.schema';
import { entityLikeSchemaStrict, entityLikeSchema } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import { SecurityBackendValidatorsEnum } from '@zaoblako/shared/modules/security/enums/security-backend-validators.enum';
import { CatalogCheckersEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-checkers.enum';
import type { MasterMilestoneFindInterface } from '@zaoblako/shared/modules/catalog/dtos/master-milestone/master-milestone-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { ChecklistTypeFindInterface } from '@zaoblako/shared/modules/project/dto/checklist-type/checklist-type-find.dto';

import * as yup from 'yup';

/** Checklist template create and update APIs request body - DTO signature */
export interface ChecklistTemplateFormInterface {
  /** Unique ID. Using to update exists checklists */
  id?: number;
  /** Name (with variables) */
  name: string;
  /** Description (with variables) */
  description?: string;
  /** Deadline settings */
  deadline: DeadlineFormInterface;
  /** Checklist type */
  type: ChecklistTypeFindInterface;
  /** Master milestone */
  masterMilestone?: MasterMilestoneFindInterface;
  /** Responsible users */
  responsible: UserFindInterface[];
  /** Checklist creator */
  createdBy?: UserFindInterface;
  /** Checklist repeat planning settings */
  planning?: PlanningUnitFormInterface;
}

/** Checklist template create and update APIs request body - DTO schema */
export const ChecklistTemplateFormDto = SerdeDtoClass.create<ChecklistTemplateFormInterface>(ProjectDtoKeysEnum.CHECKLIST_TEMPLATE_FORM_DTO, {
  id: {
    optional: true,
    label: 'Номер',
    serializer: idLikeSchema,
    checkers: [ProjectCheckersEnum.CHECKLIST_TEMPLATE_ASSERT_EXISTS_BY_ID],
  },
  name: {
    label: 'Название',
    serializer: nonEmptyStringSchema,
  },
  description: {
    optional: true,
    label: 'Описание',
    serializer: yup.string(),
  },
  deadline: {
    label: 'Настройки дедлайна',
    nested: DeadlineFormDto,
    serializer: (_, nested) => nested,
  },
  type: {
    label: 'Тип события',
    serializer: entityLikeSchemaStrict,
    checkers: [ProjectCheckersEnum.CHECKLIST_TYPE_ASSERT_EXISTS_BY_DTO],
  },
  masterMilestone: {
    optional: true,
    label: 'Мастер-веха',
    serializer: entityLikeSchema,
    checkers: [CatalogCheckersEnum.MASTER_MILESTONE_ASSERT_EXISTS_BY_DTO],
  },
  responsible: {
    label: 'Ответственные',
    serializer: yup
      .array(entityLikeSchemaStrict)
      .min(1, DtoMessageEnum.EMPTY_ARRAY),
    checkers: [SecurityBackendValidatorsEnum.USER_ASSERT_EXISTS_BY_DTO],
  },
  createdBy: {
    optional: true,
    label: 'Автор',
    serializer: entityLikeSchema,
    checkers: [SecurityBackendValidatorsEnum.USER_ASSERT_EXISTS_BY_DTO],
  },
  planning: {
    optional: true,
    label: 'Планирование повторов события',
    nested: PlanningUnitFormDto,
    serializer: (_, nested) => nested,
  },
});
